// useQueries Hook - Fetches assigned and raised queries with filters and pagination
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  QueryEntity,
  QueryFilters,
  QueryPagination,
} from "../../domain/entities/QueryEntity";
import { QueryCountsEntity } from "../../domain/entities/QueryCountsEntity";
import { GetAssignedQueriesUseCase } from "../../domain/use-cases/GetAssignedQueriesUseCase";
import { GetRaisedQueriesUseCase } from "../../domain/use-cases/GetRaisedQueriesUseCase";
import { GetMyCountsUseCase } from "../../domain/use-cases/GetMyCountsUseCase";
import { QueriesRepositoryImpl } from "../../data/repositories/QueriesRepositoryImpl";
import { queriesApi } from "../../data/api/queriesApi";

const PAGE_SIZE = 5;

const createEmptyPagination = (): QueryPagination => ({
  limit: PAGE_SIZE,
  offset: 0,
  hasMore: false,
  total: 0,
});

export const useQueries = (
  userId: number | undefined,
  filters?: QueryFilters,
  activeView: "assigned" | "raised" = "assigned"
) => {
  const [assignedQueries, setAssignedQueries] = useState<QueryEntity[]>([]);
  const [assignedPagination, setAssignedPagination] = useState<QueryPagination>(
    createEmptyPagination()
  );
  const [raisedQueries, setRaisedQueries] = useState<QueryEntity[]>([]);
  const [raisedPagination, setRaisedPagination] = useState<QueryPagination>(
    createEmptyPagination()
  );
  const [counts, setCounts] = useState<QueryCountsEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assignedOffset, setAssignedOffset] = useState(0);
  const [raisedOffset, setRaisedOffset] = useState(0);

  const repository = useMemo(() => new QueriesRepositoryImpl(queriesApi), []);

  const getAssignedUseCase = useMemo(
    () => new GetAssignedQueriesUseCase(repository),
    [repository]
  );

  const getRaisedUseCase = useMemo(
    () => new GetRaisedQueriesUseCase(repository),
    [repository]
  );

  const getCountsUseCase = useMemo(
    () => new GetMyCountsUseCase(repository),
    [repository]
  );

  const fetchQueries = useCallback(async () => {
    if (!userId) {
      setAssignedQueries([]);
      setAssignedPagination(createEmptyPagination());
      setRaisedQueries([]);
      setRaisedPagination(createEmptyPagination());
      setCounts(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const countsPromise = getCountsUseCase.execute();
      const activeQueryPromise =
        activeView === "assigned"
          ? getAssignedUseCase.execute(
              userId,
              filters,
              PAGE_SIZE,
              assignedOffset
            )
          : getRaisedUseCase.execute(userId, filters, PAGE_SIZE, raisedOffset);

      const [countsResult, activeResult] = await Promise.all([
        countsPromise,
        activeQueryPromise,
      ]);

      if (countsResult.isSuccess) {
        setCounts(countsResult.value);
      } else {
        console.error("Failed to fetch counts:", countsResult.error);
        setCounts(null);
      }

      if (activeView === "assigned") {
        if (activeResult.isSuccess) {
          setAssignedQueries(activeResult.value.queries);
          setAssignedPagination(activeResult.value.pagination);
        } else {
          console.error(
            "Failed to fetch assigned queries:",
            activeResult.error
          );
          setAssignedQueries([]);
          setAssignedPagination(createEmptyPagination());
        }
      } else {
        if (activeResult.isSuccess) {
          setRaisedQueries(activeResult.value.queries);
          setRaisedPagination(activeResult.value.pagination);
        } else {
          console.error("Failed to fetch raised queries:", activeResult.error);
          setRaisedQueries([]);
          setRaisedPagination(createEmptyPagination());
        }
      }

      if (!countsResult.isSuccess || !activeResult.isSuccess) {
        setError("Failed to fetch some query data");
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch queries");
    } finally {
      setLoading(false);
    }
  }, [
    userId,
    filters,
    activeView,
    assignedOffset,
    raisedOffset,
    getAssignedUseCase,
    getRaisedUseCase,
    getCountsUseCase,
  ]);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  useEffect(() => {
    setAssignedOffset(0);
    setRaisedOffset(0);
  }, [filters, userId]);

  const goToNextAssignedPage = () => {
    if (!assignedPagination.hasMore) {
      return;
    }

    const step = Math.max(assignedPagination.limit, PAGE_SIZE);
    setAssignedOffset((prev) => prev + step);
  };

  const goToPreviousAssignedPage = () => {
    const step = Math.max(assignedPagination.limit, PAGE_SIZE);
    setAssignedOffset((prev) => Math.max(prev - step, 0));
  };

  const goToNextRaisedPage = () => {
    if (!raisedPagination.hasMore) {
      return;
    }

    const step = Math.max(raisedPagination.limit, PAGE_SIZE);
    setRaisedOffset((prev) => prev + step);
  };

  const goToPreviousRaisedPage = () => {
    const step = Math.max(raisedPagination.limit, PAGE_SIZE);
    setRaisedOffset((prev) => Math.max(prev - step, 0));
  };

  const goToAssignedPage = (page: number) => {
    const step = Math.max(assignedPagination.limit, PAGE_SIZE);
    const normalizedPage = Math.max(page, 1);
    const maxOffset = Math.max(assignedPagination.total - step, 0);
    const targetOffset = Math.min((normalizedPage - 1) * step, maxOffset);
    setAssignedOffset(targetOffset);
  };

  const goToRaisedPage = (page: number) => {
    const step = Math.max(raisedPagination.limit, PAGE_SIZE);
    const normalizedPage = Math.max(page, 1);
    const maxOffset = Math.max(raisedPagination.total - step, 0);
    const targetOffset = Math.min((normalizedPage - 1) * step, maxOffset);
    setRaisedOffset(targetOffset);
  };

  return {
    assignedQueries,
    raisedQueries,
    counts,
    loading,
    error,
    refetch: fetchQueries,
    assignedPagination,
    raisedPagination,
    goToNextAssignedPage,
    goToPreviousAssignedPage,
    goToNextRaisedPage,
    goToPreviousRaisedPage,
    goToAssignedPage,
    goToRaisedPage,
  };
};
